import React from 'react'

import {
  Box,
  Display,
  DisplayPosition,
  FontFamily,
  FontWeight,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { DietaryWarningTag } from '../Tags'

type RecipeTagColor = {
  light: string
  dark?: string
}

export type RecipeDietaryOverlayTag = {
  type: string
  text: string
}

export type RecipeTagLabel = {
  type: string
  text: string
  color?: RecipeTagColor
  backgroundColor?: RecipeTagColor
}

type InlinePosition = 'left' | 'right'
type RecipeTagLabelVariant = 'default' | 'stacked'

const pillRadiusByVariant: Record<RecipeTagLabelVariant, React.CSSProperties> = {
  default: {
    borderRadius: '4px',
  },
  stacked: {
    borderRadius: '0 4px 4px 0',
  },
}

const DietaryBadge = withCitrusTheme(Box, (theme) => ({
  width: '28px',
  height: '28px',
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.tokens.colors.ColdGrey_700,
  color: theme.tokens.colors.White,
}))

const LabelPill = withCitrusTheme(Box, () => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '24px',
  minWidth: 0,
  maxWidth: '100%',
  padding: '0 0.5rem',
}))

const getInlinePositionStyle = (position: InlinePosition, offset: string | number) =>
  position === 'right' ? { right: offset } : { left: offset }

export const RecipeDietaryTagOverlay = ({
  tags,
  inlinePosition = 'right',
  inset = '1rem',
  dataTestId = 'recipe-tile-dietary-tags',
  style,
}: {
  tags: RecipeDietaryOverlayTag[]
  inlinePosition?: InlinePosition
  inset?: string | number
  dataTestId?: string
  style?: React.CSSProperties
}) => {
  if (tags.length === 0) {
    return null
  }

  return (
    <Box
      data-testid={dataTestId}
      display={Display.Flex}
      gap={1}
      position={DisplayPosition.Absolute}
      style={{
        top: inset,
        zIndex: 2,
        ...getInlinePositionStyle(inlinePosition, inset),
        ...style,
      }}
    >
      {tags.map((tag) => (
        <DietaryBadge key={tag.type}>
          <Text
            size={2}
            fontFamily={FontFamily.Bold}
            fontWeight={FontWeight.Bold}
            style={{ color: 'inherit', lineHeight: '1' }}
          >
            {tag.text}
          </Text>
        </DietaryBadge>
      ))}
    </Box>
  )
}

export const RecipeTagLabels = ({
  tags,
  isOutOfStock = false,
  reserveEndSpace = false,
  stacked = false,
  inset = '1rem',
  dataTestId = 'recipe-tile-general-labels',
  style,
}: {
  tags: RecipeTagLabel[]
  isOutOfStock?: boolean
  reserveEndSpace?: boolean
  stacked?: boolean
  inset?: string | number
  dataTestId?: string
  style?: React.CSSProperties
}) => {
  if (!isOutOfStock && tags.length === 0) {
    return null
  }

  const wrapperStyle: React.CSSProperties = {
    position: DisplayPosition.Absolute,
    top: inset,
    left: inset,
    zIndex: 12,
    minWidth: 0,
    maxWidth: reserveEndSpace ? 'calc(100% - 88px)' : 'calc(100% - 2rem)',
    overflow: 'hidden',
    ...style,
  }

  const tagVariant: RecipeTagLabelVariant = stacked ? 'stacked' : 'default'

  if (isOutOfStock) {
    return (
      <Box data-testid="sold-out-tag" style={wrapperStyle}>
        <LabelPill
          style={{
            backgroundColor: '#4F5C68',
            ...pillRadiusByVariant[tagVariant],
            color: '#FFFFFF',
          }}
        >
          <Text size={2} style={{ color: 'inherit', lineHeight: '24px' }}>
            Sold Out
          </Text>
        </LabelPill>
      </Box>
    )
  }

  return (
    <Box
      data-testid={dataTestId}
      display={Display.Flex}
      gap={stacked ? 2 : 1}
      style={{
        flexDirection: stacked ? 'column' : undefined,
        alignItems: stacked ? 'flex-start' : undefined,
        ...wrapperStyle,
      }}
    >
      {tags.map((label) => (
        <RecipeTagLabelPill
          key={`${label.type}-${label.text}`}
          label={label}
          variant={tagVariant}
        />
      ))}
    </Box>
  )
}

const RecipeTagLabelPill = ({
  label,
  variant = 'default',
}: {
  label: RecipeTagLabel
  variant?: RecipeTagLabelVariant
}) => {
  if (label.type === 'invalidPreference') {
    return <DietaryWarningTag text={label.text} variant={variant} />
  }

  if (!label.color || !label.backgroundColor) {
    return null
  }

  return (
    <LabelPill
      style={{
        backgroundColor: label.backgroundColor.light,
        ...pillRadiusByVariant[variant],
        color: label.color.light,
      }}
    >
      <Text
        size={2}
        style={{
          color: 'inherit',
          lineHeight: '24px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {label.text}
      </Text>
    </LabelPill>
  )
}
